// Provenance: repo:tds-reconciliation-portal:frontend/src/components/DocumentsSection.tsx
// (ADR-0005) — restyled on the estate tokens (ADR-0010). Ids became UUID strings; the
// href downloads became authenticated flows (DownloadButton / the streamed helpers) — an
// anchor cannot carry a Bearer.
//
// The deliverables and the source files, on the page that shows the reports:
//   - what the run PRODUCED (workbook, memo, register) — what goes to the client
//   - what this session PUT IN, as parsed and as uploaded
// They are the two ends of the same run, and neither is a report you can read
// on this page.
import { AlertTriangle, Download, RefreshCw } from 'lucide-react'
import type { ReactNode } from 'react'
import { useState } from 'react'

import { ApiError, downloadDocument, downloadFile, downloadUpload } from '../api/client'
import { useDocuments, useRawSummary } from '../api/hooks'
import { useRegenerateMutation } from '../api/resolutions'
import type { DocumentInfo } from '../api/types'
import { fmtDateTime, shortSha } from '../format'
import { useWorkspaceSession, visibleRawEntries } from '../workspace/workspaceSession'
import { Chip } from './badges'
import { Btn } from './Modal'
import { showToast } from './toast'

/** What to say when generated files no longer match the run, or null.
 *
 *  It names the count because "some documents are out of date" leaves the
 *  reader counting rows to find out which, and it names the remedy because a
 *  warning without one sends them hunting for the button.
 */
export function staleNote(docs: DocumentInfo[]): string | null {
  const n = docs.filter((d) => d.stale).length
  if (!n) return null
  return `${n} of ${docs.length} ${n === 1 ? 'file was' : 'files were'} generated `
    + 'before the latest work on this run. Regenerate before sending anything out.'
}

const DOC_LABELS: Record<string, string> = {
  workbook: 'Reconciliation workbook (xlsx)',
  memo: 'Audit memo (docx)',
  resolution_register: 'Resolution register (xlsx)',
}

interface Row {
  label: string
  detail: ReactNode
  links: { label: string; run: () => Promise<void> }[]
}

function LinkBtn({ label, run }: { label: string; run: () => Promise<void> }) {
  const [busy, setBusy] = useState(false)
  return (
    <Btn size="xs" loading={busy}
      onClick={() => {
        setBusy(true)
        run()
          .catch((e) => showToast({
            color: 'red', title: 'Download failed',
            message: e instanceof ApiError ? e.message : String(e),
          }))
          .finally(() => setBusy(false))
      }}>
      <Download size={13} /> {label}
    </Btn>
  )
}

function FileTable({ title, rows, action, alert }: {
  title: string
  rows: Row[]
  action?: ReactNode
  alert?: ReactNode
}) {
  if (!rows.length && !action) return null
  return (
    <div className="h-full rounded-lg border border-border p-4">
      <div className="mb-3 flex flex-nowrap items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-foreground">
          {title} <Chip tone="blue" className="ml-1">{rows.length}</Chip>
        </h3>
        {action}
      </div>
      {alert}
      {!rows.length && <p className="text-sm text-muted-foreground">Nothing here yet.</p>}
      {rows.length > 0 && (
        <table className="w-full text-sm">
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-border first:border-0">
                <td className="py-2">
                  <p className="text-sm font-semibold text-foreground">{r.label}</p>
                  <div className="text-xs text-muted-foreground">{r.detail}</div>
                </td>
                <td className="py-2" style={{ width: 300 }}>
                  <span className="flex flex-wrap justify-end gap-1.5">
                    {r.links.map((l) => (
                      <LinkBtn key={l.label} label={l.label} run={l.run} />
                    ))}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

export function DocumentsSection({
  runId, periodId,
}: {
  runId: string | null
  periodId: string
}) {
  const docsQ = useDocuments(runId ?? '', runId != null)
  const rawQ = useRawSummary(periodId)
  const regen = useRegenerateMutation(runId ?? '')
  const { sides } = useWorkspaceSession()

  // The timestamp answers "is this current?" at a glance and stays visible;
  // the sha only matters when proving WHICH file was sent, so it moves to
  // hover rather than competing with the date on every row.
  const docs: Row[] = (docsQ.data ?? []).map((d) => ({
    label: DOC_LABELS[d.kind] ?? d.kind,
    detail: (
      <span className="flex flex-nowrap items-center gap-1.5">
        <span>generated {fmtDateTime(d.generated_at)}</span>
        {d.stale && <Chip tone="orange">out of date</Chip>}
        <span
          className="cursor-help border-b border-dotted border-current font-mono text-xs text-muted-foreground"
          title={`sha256 ${d.sha256}`}
        >
          {shortSha(d.sha256)}
        </span>
      </span>
    ),
    links: [{ label: 'Download', run: () => downloadDocument(d.id) }],
  }))
  const stale = staleNote(docsQ.data ?? [])

  // only this session's files are offered: a download link is as much a claim
  // that the data is yours to hand over as a table of it is
  const raw: Row[] = visibleRawEntries(rawQ.data ?? [], sides(periodId)).map((e) => ({
    label: `${e.side === '26as' ? '26AS statement rows' : 'Book Data rows'} — ${e.filename}`,
    detail: `${e.rows} rows as parsed`,
    links: [
      { label: 'Excel', run: () => downloadFile(`/uploads/${e.upload_id}/rows/export?format=xlsx`) },
      { label: 'CSV', run: () => downloadFile(`/uploads/${e.upload_id}/rows/export?format=csv`) },
      { label: 'Original file', run: () => downloadUpload(e.upload_id) },
    ],
  }))

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold text-foreground">Documents &amp; source files</h2>
      {/* Two independent cards, not a two-column table: Run documents is
          always three, while Source files is however many this session
          uploaded — different heights, and that is fine. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <FileTable
          title="Run documents"
          rows={docs}
          alert={stale && (
            <p className="mb-3 flex items-start gap-2 rounded-md border border-orange-300 bg-orange-50 p-2.5 text-xs text-orange-900">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              {stale}
            </p>
          )}
          action={runId != null && (
            <Btn
              // Filled and coloured once something is out of date: this
              // button is the remedy, and a quiet control beside a warning
              // reads as though the warning were someone else's problem.
              size="xs"
              kind={stale ? 'warning' : 'default'}
              loading={regen.isPending}
              onClick={() => regen.mutate(undefined, {
                onSuccess: () => showToast({
                  color: 'green',
                  title: 'Documents regenerated with the current resolution state',
                }),
                onError: (e) => showToast({ color: 'red', title: String(e) }),
              })}
            >
              <RefreshCw size={14} /> Regenerate
            </Btn>
          )}
        />
        <FileTable title="Source files (this session)" rows={raw} />
      </div>
    </div>
  )
}
